import { Grid } from "@mui/material";
import VuiAutocomplete from "components/AutoComplete";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiSwitch from "components/VuiSwitch";
import VuiTypography from "components/VuiTypography";
import { FaPlus, FaTrash } from "react-icons/fa";

const DynamicFieldBuilder = ({
  title,
  description,
  fields,
  fieldKey,
  setFieldValue,
  touched,
  errors,
}) => {
  const EMPTY_CREDENTIAL_FIELD = {
    name: "",
    label: "",
    type: "string",
    required: false,
    secret: false,
    placeholder: "",
    helpText: "",
    options: [],
  };

  const FIELD_TYPE_OPTIONS = [
    { value: "string", label: "String" },
    { value: "password", label: "Password" },
    { value: "number", label: "Number" },
    { value: "boolean", label: "Boolean" },
    { value: "json", label: "JSON" },
  ];
  const addField = () => {
    setFieldValue(fieldKey, [...fields, { ...EMPTY_CREDENTIAL_FIELD }]);
  };

  const updateField = (index, key, value) => {
    setFieldValue(`${fieldKey}.${index}.${key}`, value);
  };

  const removeField = (index) => {
    const updated = [...fields];
    updated.splice(index, 1);
    setFieldValue(fieldKey, updated);
  };

  return (
    <VuiBox mt={2}>
      <VuiBox>
        <VuiTypography variant="button">{title}</VuiTypography>
      </VuiBox>

      {description && (
        <VuiBox>
          <VuiTypography variant="caption" opacity={0.7}>
            {description}
          </VuiTypography>
        </VuiBox>
      )}

      {fields.map((field, index) => (
        <VuiBox key={index} mb={0.5}>
          <Grid container spacing={1} alignItems="center">
            {/* Field Name */}
            <Grid item xs={12} md={3}>
              <VuiBox>
                <VuiTypography
                  variant="caption"
                  color={
                    touched?.credentialFields?.[index]?.name &&
                    errors?.credentialFields?.[index]?.name
                      ? "error"
                      : "primary"
                  }
                >
                  Field Name *
                </VuiTypography>

                <VuiInput
                  gradient
                  fullWidth
                  value={field.name}
                  onChange={(e) => updateField(index, "name", e.target.value)}
                  onBlur={() => setFieldValue(`credentialFields.${index}.name`, field.name)}
                />
              </VuiBox>
            </Grid>

            {/* Field Label */}
            <Grid item xs={12} md={2}>
              <VuiBox>
                <VuiTypography
                  variant="caption"
                  color={
                    touched?.credentialFields?.[index]?.label &&
                    errors?.credentialFields?.[index]?.label
                      ? "error"
                      : "primary"
                  }
                >
                  Field Label *
                </VuiTypography>

                <VuiInput
                  gradient
                  fullWidth
                  value={field.label}
                  onChange={(e) => updateField(index, "label", e.target.value)}
                  onBlur={() => setFieldValue(`credentialFields.${index}.label`, field.label)}
                />
              </VuiBox>
            </Grid>

            {/* Field Type */}
            <Grid item xs={12} md={2}>
              <VuiBox>
                <VuiTypography variant="caption">Field Type *</VuiTypography>

                <VuiAutocomplete
                  options={FIELD_TYPE_OPTIONS}
                  value={FIELD_TYPE_OPTIONS.find((opt) => opt.value === field.type) || null}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  placeholder="Select field type"
                  minWidth={50}
                  gradient
                  onChange={(_, selected) => {
                    updateField(index, "type", selected?.value || "");
                  }}
                />

                {touched?.credentialFields?.[index]?.type &&
                  errors?.credentialFields?.[index]?.type && (
                    <VuiTypography variant="caption" color="error">
                      {errors.credentialFields[index].type}
                    </VuiTypography>
                  )}
              </VuiBox>
            </Grid>

            {/* Required */}
            <Grid item xs={12} md={2}>
              <VuiBox>
                <VuiTypography variant="caption" sx={{ visibility: "hidden" }}>
                  Spacer
                </VuiTypography>

                <VuiBox display="flex" alignItems="center" gap={1}>
                  <VuiSwitch
                    color="success"
                    checked={field.required}
                    onChange={() => updateField(index, "required", !field.required)}
                  />
                  <VuiTypography variant="caption">Required</VuiTypography>
                </VuiBox>
              </VuiBox>
            </Grid>

            {/* Secret */}
            <Grid item xs={12} md={2}>
              <VuiBox>
                <VuiTypography variant="caption" sx={{ visibility: "hidden" }}>
                  Spacer
                </VuiTypography>

                <VuiBox display="flex" alignItems="center" gap={1}>
                  <VuiSwitch
                    color="success"
                    checked={field.secret}
                    onChange={() => updateField(index, "secret", !field.secret)}
                  />
                  <VuiTypography variant="caption">Secret</VuiTypography>
                </VuiBox>
              </VuiBox>
            </Grid>

            {/* Remove Button */}
            <Grid
              item
              xs={12}
              md="auto"
              sx={{
                flex: "0 0 auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              <VuiBox>
                <VuiTypography variant="caption" sx={{ visibility: "hidden" }}>
                  Spacer
                </VuiTypography>

                <VuiBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: 38 }}
                >
                  <VuiButton
                    variant="outlined"
                    color="error"
                    onClick={() => removeField(index)}
                    sx={{
                      minWidth: "32px",
                      width: "32px",
                      height: "32px",
                      padding: "0",
                      borderRadius: "8px",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaTrash size={12} />
                  </VuiButton>
                </VuiBox>
              </VuiBox>
            </Grid>
          </Grid>
        </VuiBox>
      ))}

      <VuiBox display="flex" justifyContent="center" mt={2}>
        <VuiButton size="small" variant="outlined" color="primary" onClick={addField}>
          <FaPlus size={12} style={{ marginRight: 6 }} />
          Add Field
        </VuiButton>
      </VuiBox>
    </VuiBox>
  );
};

export default DynamicFieldBuilder;
